import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Icons } from "@learningblocks/exp-constants";
const { ChevronDownSolid: OpenIcon } = Icons;
import "./style.scss";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { HighlightProvider as SharedHighlightProvider } from "@learningblocks/shared-components";
import { useHighlight } from "@learningblocks/shared/glossary";
import { useIntl } from "react-intl";
import { Equation } from "@learningblocks/protocol-components/lib/Equation";

const ENTER_KEY = 13;

export function ContentDropdown(props) {
  const ref = useRef();
  const intl = useIntl();

  const [open, setOpen] = useState(props.open);
  const [contentHeight, setContentHeight] = useState("none");

  useEffect(() => {
    updateContentHeight();

    window.onresize = function () {
      updateContentHeight();
    };
  }, []);

  const updateContentHeight = () => {
    if (ref.current) {
      setContentHeight(ref.current.scrollHeight + (!open ? 16 : 0));
    }
  };

  const { onPlainTextClick } =
    React.useContext(SharedHighlightProvider.Context) || useHighlight();

  const onClickHighlight = (contentId, event) => {
    onPlainTextClick(contentId, event);
  };

  const toggleOpen = () => {
    updateContentHeight();
    setOpen(!open);
  };

  return (
    <div className="ContentDropdown_container">
      <div
        className="ContentDropdown_btn"
        role="checkbox"
        aria-checked={open}
        tabIndex="0"
        aria-label={intl.formatMessage(
          {
            id: "Expand the {block} section.",
            defaultMessage: "Expand the {block} section.",
            description: "",
          },
          {
            block:
              props.contentDetails.label !== undefined
                ? props.contentDetails.label
                : intl.formatMessage({
                    id: "Dropdown",
                    defaultMessage: "Dropdown",
                    description: "",
                  }),
          }
        )}
        onClick={() => toggleOpen()}
        onKeyDown={(e) => {
          if (e.keyCode === ENTER_KEY) {
            toggleOpen();
          }
        }}
      >
        {props.contentDetails.label === undefined
          ? open
            ? intl.formatMessage({
                id: "Collapse",
                defaultMessage: "Collapse",
                description: "",
              })
            : intl.formatMessage({
                id: "Expand",
                defaultMessage: "Expand",
                description: "",
              })
          : intl.formatMessage({
              id: props.contentDetails.label,
              defaultMessage: props.contentDetails.label,
              description: "",
            })}
        <OpenIcon
          className={`ContentDropdown_icon ${open ? "open" : "closed"}`}
          alt=""
        />
      </div>
      <div
        ref={ref}
        className={`ContentDropdown_content ${open ? "open" : "closed"}`}
        aria-hidden={!open}
        style={{
          maxHeight: open ? contentHeight : "0px",
        }}
      >
        {typeof props.contentDetails.content === "object" && (
          <RichText
            onClickHighlight={onClickHighlight}
            {...props.contentDetails.content}
          />
        )}
        {typeof props.contentDetails.content === "string" && (
          <Equation text={props.contentDetails.content} />
        )}
      </div>
    </div>
  );
}

ContentDropdown.propTypes = {
  contentDropdown: PropTypes.shape({
    /**
     * Label that is displayed as the header for the Dropdown
     */
    label: PropTypes.string,
    /**
     * Content that is given to the particular dropdown.
     */
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      .isRequired,
    /**
     * Boolean that is used to check whether the dropdown is open or closed
     */
    open: PropTypes.bool.isRequired,
  }),
};

ContentDropdown.defaultProps = {
  contentDropdown: null,
};
